import React, { useId, useState } from 'react';
import { Button, Flex, Popover, TimePicker } from 'antd';
import { Dayjs } from 'dayjs';
import {
  secondsToDayjs,
  dayjsToSeconds,
} from '@/pages/Logiblocks/dateTimeUtils';
import { EntryTimeWindow } from './dateSetTypes';
import PrimaryButton from './PrimaryButton';
import styles from './EntryTimeRangePopover.module.css';

interface EntryTimeRangePopoverProps {
  window: EntryTimeWindow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (window: EntryTimeWindow) => void;
  /** Reverts the entry to All day. Only shown when the entry already has a
   * range set. */
  onClear: () => void;
  children: React.ReactNode;
}

const DEFAULT_START_SECONDS = 6 * 3600; // 6:00 AM
const DEFAULT_END_SECONDS = 21 * 3600; // 9:00 PM

function startOf(window: EntryTimeWindow): Dayjs {
  return secondsToDayjs(window.startSeconds ?? DEFAULT_START_SECONDS);
}

function endOf(window: EntryTimeWindow): Dayjs {
  return secondsToDayjs(window.endSeconds ?? DEFAULT_END_SECONDS);
}

interface TimeFieldProps {
  label: string;
  value: Dayjs;
  onChange: (value: Dayjs) => void;
}

const TimeField: React.FC<TimeFieldProps> = ({ label, value, onChange }) => {
  const id = useId();
  return (
    <Flex vertical gap={4} flex={1}>
      <label htmlFor={id} className={styles.label}>
        {label}
      </label>
      <TimePicker
        id={id}
        value={value}
        onChange={(v) => v && onChange(v)}
        use12Hours
        format='hh:mm A'
        minuteStep={15}
        allowClear={false}
        suffixIcon={null}
        getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
      />
    </Flex>
  );
};

/** Anchors a Start/End time-range editor to a chip. Save commits the range
 * onto the chip; closing the popover discards, and Clear reverts the chip to
 * All day. */
const EntryTimeRangePopover: React.FC<EntryTimeRangePopoverProps> = ({
  window,
  open,
  onOpenChange,
  onSave,
  onClear,
  children,
}) => {
  const [start, setStart] = useState<Dayjs>(() => startOf(window));
  const [end, setEnd] = useState<Dayjs>(() => endOf(window));

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setStart(startOf(window));
      setEnd(endOf(window));
    }
    onOpenChange(next);
  };

  const invalid = !end.isAfter(start, 'minute');

  const handleSave = () => {
    if (invalid) return;
    onSave({
      allDay: false,
      startSeconds: dayjsToSeconds(start),
      endSeconds: dayjsToSeconds(end),
    });
    onOpenChange(false);
  };

  const content = (
    <div className={styles.panel}>
      <Flex gap={16}>
        <TimeField label='Start' value={start} onChange={setStart} />
        <TimeField label='End' value={end} onChange={setEnd} />
      </Flex>
      {invalid && (
        <span className={styles.error}>End time must be after start time</span>
      )}
      <Flex justify='flex-end' gap={8} className={styles.footer}>
        {!window.allDay && (
          <Button type='text' onClick={onClear}>
            Clear
          </Button>
        )}
        <PrimaryButton disabled={invalid} onClick={handleSave}>
          Save
        </PrimaryButton>
      </Flex>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger='click'
      placement='bottomLeft'
      content={content}
      // Portal into the picker panel, not document.body: the panel renders
      // inside the Dropdown's transformed motion wrapper, which makes a
      // body-portaled popover mis-position (off-screen). The container is
      // looked up here because antd's first call passes a null trigger.
      getPopupContainer={() =>
        document.querySelector<HTMLElement>('.lb-date-picker-panel') ??
        document.body
      }
    >
      {children}
    </Popover>
  );
};

export default EntryTimeRangePopover;
